import { versionProductImage } from "../../utils/productImageVersion";
import { useCategoryHistoryState } from "../categoryHistory";
// src/pages/BolsosProductList.jsx
import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ProductsContext } from "../../../context/ProductContext";
import { resolveProductCardPricing } from "../../utils/productPricing";
import "../ProductList.css";
import {
  buildCategorySearchParams,
  categoryFromSubcategory,
  matchesCategoryFilters,
  readCategoryFilters,
  toCategoryFilterSlug,
} from "../categoryUrlFilters";

/* ---------- Helpers ---------- */
const isHttp = (s) => typeof s === "string" && /^https?:\/\//i.test(s);
const normalizeText = (s = "") =>
  String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/* --- Google Drive helpers --- */
const parseDriveId = (urlOrId = "") => {
  const s = String(urlOrId).trim();
  const m =
    s.match(/\/d\/([a-zA-Z0-9_-]+)/) || s.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  return m ? m[1] : s;
};
const driveView = (urlOrId) =>
  `https://drive.google.com/uc?export=view&id=${parseDriveId(urlOrId)}`;
const driveThumb = (urlOrId, w = 1600) =>
  `https://drive.google.com/thumbnail?authuser=0&sz=w${w}&id=${parseDriveId(urlOrId)}`;
const driveLH3 = (urlOrId, w = 1600) =>
  `https://lh3.googleusercontent.com/d/${parseDriveId(urlOrId)}=w${w}`;
const driveFallbackSrc = (urlOrId) => {
  const id = parseDriveId(urlOrId);
  return [driveLH3(id), driveThumb(id), driveView(id)];
};
const resolveImage = (img) => {
  if (!img) return null;
  if (/drive\.google\.com/.test(img)) return driveFallbackSrc(img);
  if (isHttp(img)) return [img];
  return null;
};

/* ---------- Department assets ---------- */
const DEPARTMENT_LABELS = {
  mujer: "Mujer",
  hombre: "Hombre",
  niña: "Niña",
  niño: "Niño",
  infantil: "Infantil",
  complementos: "Complementos",
  unisex: "Unisex",
  otros: "Otros",
};

const BAGS_DEPARTMENT_GROUPS = [
  { key: "mujer", label: "Mujer" },
  { key: "hombre", label: "Hombre" },
  { key: "niño", label: "Niño" },
  { key: "niña", label: "Niña" },
  { key: "unisex", label: "Unisex" },
];

const DEPARTMENT_IMAGE_SOURCES = (slug) => {
  switch (slug) {
    case "mujer":
      return ["/images/bags/mujer.jpg", "/images/departments/mujer.jpg"];
    case "hombre":
      return ["/images/bags/hombre.jpg", "/images/departments/hombre.jpg"];
    case "niña":
      return [
        "/images/bags/nina.jpg",
        "/images/departments/nina.jpg",
        "/images/departments/niña.jpg",
        "/images/departments/infantil.jpg",
        "/images/departments/otros.jpg",
      ];
    case "niño":
      return [
        "/images/bags/nino.jpg",
        "/images/departments/nino.jpg",
        "/images/departments/niño.jpg",
        "/images/departments/infantil.jpg",
        "/images/departments/otros.jpg",
      ];
    case "infantil":
      return [
        "/images/departments/infantil.jpg",
        "/images/departments/ninos.jpg",
        "/images/departments/otros.jpg",
      ];
    case "complementos":
      return ["/images/departments/otros.jpg"];
    case "unisex":
      return [
        "/images/bags/unisex.jpg",
        "/images/departments/unisex.jpg",
        "/images/departments/otros.jpg",
    ];
    default:
      return ["/images/departments/otros.jpg"];
  }
};

function DepartmentChipImage({ slug, alt }) {
  const candidates = DEPARTMENT_IMAGE_SOURCES(slug);
  const idxRef = useRef(0);
  const [src, setSrc] = useState(candidates[0]);

  const handleError = () => {
    const next = idxRef.current + 1;
    if (next < candidates.length) {
      idxRef.current = next;
      setSrc(candidates[next]);
    }
  };

  return <img src={src} alt={alt} loading="lazy" onError={handleError} />;
}

/* -------- Slug de departamento -------- */
const depToSlug = (p) => {
  const d = normalizeText(p?.department || "");
  if (d.includes("mujer") || d.includes("dama") || d.includes("damas") || d.includes("women"))
    return "mujer";
  if (d.includes("hombre") || d.includes("caballero") || d.includes("caballeros") || d.includes("men"))
    return "hombre";
  if (d.includes("nina") || d.includes("ninas") || d.includes("girl") || d.includes("girls"))
    return "niña";
  if (d.includes("nino") || d.includes("ninos") || d.includes("boy") || d.includes("boys"))
    return "niño";
  if (d.includes("infantil") || d.includes("kids")) return "infantil";
  if (d.includes("complement") || d.includes("accesorio")) return "complementos";
  if (d.includes("unisex")) return "unisex";
  return "otros";
};

/* -------- Price & variants -------- */
const sizesOf = (variants = []) =>
  variants
    .flatMap((v) => (v.tallas || v.sizes || []).map((s) => s?.size))
    .filter(Boolean);

const colorsOf = (variants = []) => variants.map((v) => v?.color).filter(Boolean);

const getPrice = (p) => {
  const n = Number(p?.price_cop);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const firstVariantImage = (variants = []) => {
  for (const v of variants) {
    if (Array.isArray(v?.images) && v.images.length && v.images[0]) {
      return v.images[0];
    }
  }
  return null;
};

const collectImages = (product, limit = 8) => {
  const pool = [];
  const pushUrl = (url) => {
    if (!url) return;
    const fall = resolveImage(url)?.map((source) => versionProductImage(source, product));
    if (!fall || !fall.length) return;
    const primary = fall[0];
    if (!primary) return;
    if (!pool.some((it) => it.primary === primary)) {
      pool.push({ primary, fallbacks: fall });
    }
  };

  (Array.isArray(product?.images) ? product.images : []).forEach(pushUrl);
  (Array.isArray(product?.variants) ? product.variants : []).forEach((v) => {
    (Array.isArray(v?.images) ? v.images : []).forEach(pushUrl);
  });

  if (!pool.length) {
    const fallback = firstVariantImage(product?.variants) || null;
    if (fallback) pushUrl(fallback);
  }
  if (!pool.length) {
    pool.push({
      primary: "https://placehold.co/600x800?text=Sin+Imagen",
      fallbacks: ["https://placehold.co/600x800?text=Sin+Imagen"],
    });
  }

  return pool.slice(0, limit);
};

/* ---------- Product Card ---------- */
const ProductCard = ({ product }) => {
  const images = collectImages(product);
  const PLACEHOLDER = "https://placehold.co/600x800?text=Sin+Imagen";

  const [idx, setIdx] = useState(0);
  const fbIdxRef = useRef(0);
  const [src, setSrc] = useState(images[0]?.fallbacks?.[0] || PLACEHOLDER);

  const touchStartX = useRef(null);

  const primarySignature = useMemo(
    () => images.map((i) => i.primary).join("|"),
    [images]
  );

  React.useEffect(() => {
    fbIdxRef.current = 0;
    setIdx(0);
    setSrc(images[0]?.fallbacks?.[0] || PLACEHOLDER);
  }, [primarySignature]);

  const goTo = (newIdx) => {
    if (!images.length) return;
    const n = ((newIdx % images.length) + images.length) % images.length;
    setIdx(n);
    fbIdxRef.current = 0;
    setSrc(images[n]?.fallbacks?.[0] || PLACEHOLDER);
  };

  const onError = () => {
    const fallbacks = images[idx]?.fallbacks || [];
    const nextFb = fbIdxRef.current + 1;
    if (nextFb < fallbacks.length) {
      fbIdxRef.current = nextFb;
      setSrc(fallbacks[nextFb]);
      return;
    }
    setSrc(PLACEHOLDER);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    const sx = touchStartX.current;
    const ex = e.changedTouches?.[0]?.clientX ?? null;
    touchStartX.current = null;
    if (sx == null || ex == null) return;
    const dx = ex - sx;
    if (Math.abs(dx) < 30) return;
    if (dx < 0) goTo(idx + 1);
    else goTo(idx - 1);
  };

  const sizes = [...new Set(sizesOf(product.variants))];
  const colors = [...new Set(colorsOf(product.variants))];
  const cardPricing = resolveProductCardPricing(product);

  return (
    <div className="col s12 m6 l4 xl3">
      <div className="card product-card-airbnb">
        <Link
          to={`/products/${product.catSlug || "Bolsos"}/${product.id}`}
          className="product-card-link"
        >
          <div
            className="card-image product-card-image-wrapper"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <img
              src={src}
              alt={product.name || "Sin nombre"}
              className="product-image"
              onError={onError}
              referrerPolicy="no-referrer"
              loading="lazy"
              decoding="async"
            />

            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className="pc-arrow pc-arrow--left"
                  aria-label="Imagen anterior"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goTo(idx - 1);
                  }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="pc-arrow pc-arrow--right"
                  aria-label="Imagen siguiente"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goTo(idx + 1);
                  }}
                >
                  ›
                </button>
              </>
            )}

            {images.length > 1 && (
              <div className="pc-dots" aria-label="Cambiar imagen">
                {images.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`pc-dot ${i === idx ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      goTo(i);
                    }}
                    aria-label={`Imagen ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="card-content product-card-content">
            <h6 className="product-name">{product.name || "Sin nombre"}</h6>

            <p className="product-price">
              {cardPricing.price !== null ? (
                <>
                  {(() => {
                    const price = cardPricing.price;
                    const old = Number(product.price_old) || 0;
                    const pct =
                      product?.discount != null
                        ? Number(product.discount)
                        : old > price
                        ? Math.round((1 - price / old) * 100)
                        : null;

                    return (
                      <>
                        <span className="price-pill">
                          {cardPricing.showFrom && <span className="price-from">Desde</span>}
                          <span className="currency">$</span>
                          <span className="amount">
                            {price.toLocaleString("es-CO")}
                          </span>
                          {pct > 0 && <span className="save">AHORRA {pct}%</span>}
                        </span>
                        {old > price && (
                          <span className="price-strike">
                            ${old.toLocaleString("es-CO")}
                          </span>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                "Precio no disponible"
              )}
            </p>

            <p className="product-sizes">
              <small>Tallas: {sizes.length ? sizes.join(", ") : "N/A"}</small>
            </p>

            <p className="product-colors">
              <small>Colores: {colors.length ? colors.join(", ") : "N/A"}</small>
            </p>
          </div>
        </Link>
      </div>
    </div>
  );
};

/* ---------- Main (solo BOLSOS) ---------- */
export default function BolsosProductList() {
  const { products, loading, error } = useContext(ProductsContext);

  // Filtra solo categoría "Bolsos" (tolerante a acentos/idiomas)
  const bolsosBase = useMemo(() => {
  const isBolso = (value = "") => {
    const text = normalizeText(value);

    return (
      text.includes("bolso") ||
      text.includes("maleta") ||
      text.includes("morral") ||
      text.includes("mochila") ||
      text.includes("viajero") ||
      text.includes("viaje")
    );
  };

  return (products || []).filter((p) => {
    return (
      isBolso(p?.category) ||
      isBolso(p?.subcategory) ||
      isBolso(p?.catSlug) ||
      isBolso(p?.name)
    );
  });
}, [products]);

  const enriched = useMemo(
    () => bolsosBase.map((p) => ({ ...p, depSlug: depToSlug(p) })),
    [bolsosBase]
  );

  const departmentChips = useMemo(() => {
    const counts = new Map();
    for (const p of enriched) {
      const s = p.depSlug || "otros";
      counts.set(s, (counts.get(s) || 0) + 1);
    }
    return BAGS_DEPARTMENT_GROUPS.map(({ key, label }) => ({
      key,
      label,
      count: counts.get(key) || 0,
    }));
  }, [enriched]);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const { department: dep, category: categoryFilter, subcategory: subFilter } = useMemo(
    () => readCategoryFilters(new URLSearchParams(searchKey), BAGS_DEPARTMENT_GROUPS.map(({ key }) => key)),
    [searchKey]
  );
  const [openGroup, setOpenGroup] = useCategoryHistoryState("openGroup", dep || null);
  const [sort, setSort] = useCategoryHistoryState("sort", "");

  useEffect(() => {
    if (dep) setOpenGroup(dep);
  }, [dep, setOpenGroup]);

  const updateFilters = (next) => setSearchParams(buildCategorySearchParams(next));

  const subcatGroups = useMemo(() => {
    const base = enriched.filter((p) => !dep || p.depSlug === dep);
    const map = new Map();

    for (const p of base) {
      const department = p.depSlug || "otros";
      if (!map.has(department)) map.set(department, new Set());
      if (p.subcategory) map.get(department).add(p.subcategory);
    }

    return BAGS_DEPARTMENT_GROUPS
      .filter(({ key }) => map.has(key))
      .map(({ key, label }) => ({
        key,
        label,
        subcats: Array.from(map.get(key)).sort((a, b) => a.localeCompare(b, "es")),
      }));
  }, [enriched, dep]);

  const filtered = useMemo(() => {
    return enriched.filter((product) => matchesCategoryFilters(product, {
      department: dep,
      category: categoryFilter,
      subcategory: subFilter,
    }));
  }, [enriched, dep, categoryFilter, subFilter]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const arr = [...filtered];
    const tieBreak = (a, b) => {
      const an = (a.name || "").localeCompare(b.name || "");
      if (an !== 0) return an;
      return String(a.id ?? "").localeCompare(String(b.id ?? ""));
    };
    const cmpAsc = (a, b) => {
      const pa = getPrice(a);
      const pb = getPrice(b);
      if (pa === null && pb === null) return tieBreak(a, b);
      if (pa === null) return 1;
      if (pb === null) return -1;
      return pa === pb ? tieBreak(a, b) : pa - pb;
    };
    const cmpDesc = (a, b) => -cmpAsc(a, b);
    arr.sort(sort === "price_asc" ? cmpAsc : cmpDesc);
    return arr;
  }, [filtered, sort]);

  if (loading) return <p className="center-align">Cargando productos...</p>;
  if (error) return <p className="center-align red-text">Error: {error}</p>;

  return (
    <div className="container product-list-container">
      <h4 className="left-align product-list-title">Bolsos</h4>

      {/* Chips por departamento (sobre los productos tech) */}
      <div className="gender-filters">
        <button
          className={`gender-chip ${dep === "" ? "gender-chip--active" : ""}`}
          onClick={() => {
            updateFilters({});
            setOpenGroup(null);
          }}
        >
          Todos <span className="count">({bolsosBase.length})</span>
        </button>

        {departmentChips.map(({ key, label, count }) => (
          <button
            key={key}
            className={`gender-chip gender-chip--image ${
              dep === key ? "gender-chip--active" : ""
            }`}
            onClick={() => {
              updateFilters({ department: key });
              setOpenGroup(key);
            }}
          >
            <DepartmentChipImage slug={key} alt={DEPARTMENT_LABELS[key] || label} />
            <span className="gender-chip-label">
              {DEPARTMENT_LABELS[key] || label}{" "}
              <span className="count">({count})</span>
            </span>
          </button>
        ))}
      </div>

      {/* Barra de controles: subcats + orden */}
      {subcatGroups.length > 0 && (
        <>
          <div className="controls-row row" style={{ alignItems: "center" }}>
            <div className="col s12 m8">
              <h5 className="subcat-heading">
                Explora por subcategoría{" "}
                {dep
                  ? `— ${
                      departmentChips.find((d) => d.key === dep)?.label ||
                      DEPARTMENT_LABELS[dep] ||
                      ""
                    }`
                  : ""}
              </h5>
            </div>
            <div className="col s12 m4 right-align">
              <label htmlFor="sort" className="sort-label">
                Ordenar:
              </label>
              <select
                id="sort"
                className="browser-default sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                aria-label="Ordenar productos por precio"
              >
                <option value="">Relevancia</option>
                <option value="price_asc">Precio: menor a mayor</option>
                <option value="price_desc">Precio: mayor a menor</option>
              </select>
            </div>
          </div>

          <div className="row subcat-grid">
            {subcatGroups.map((g) => {
              const isOpen = openGroup === g.key;
              return (
                <div className="col s12 m6 l4 xl3" key={g.key}>
                  <div className={`subcat-card text-only card-border accordion-item ${isOpen ? "open" : ""}`}>
                    <button type="button" className="accordion-header" onClick={() => setOpenGroup(isOpen ? null : g.key)} aria-expanded={isOpen}>
                      <h6 className="subcat-title">{g.label}</h6>
                      <span className="accordion-arrow">⌄</span>
                    </button>
                    <div className="subcat-chips">
                      {g.subcats.slice(0, 12).map((s) => {
                        const subcategorySlug = toCategoryFilterSlug(s);
                        const categorySlug = categoryFromSubcategory(s);
                        const active = subFilter
                          ? subFilter === subcategorySlug
                          : categoryFilter === categorySlug && subcategorySlug === categorySlug;
                        return <button key={s} type="button" className={`subcat-chip ${active ? "active" : ""}`} onClick={() => updateFilters(active ? { department: dep } : { department: dep || g.key, category: categorySlug, subcategory: subcategorySlug })} aria-pressed={active}>{s}</button>;
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Grilla */}
      <div className="product-grid">
        {sorted.length ? (
          sorted.map((p) => (
            <ProductCard key={`${p.catSlug || "Bolsos"}-${p.id}`} product={p} />
          ))
        ) : (
          <p className="center-align">No se encontraron productos de Bolsos</p>
        )}
      </div>
    </div>
  );
}
